import {NodeDisplay} from './NodeDisplay';
import {JSX, RefObject} from 'react';
import {XmlElementNode} from 'simple_xml';
import {DeleteButton, SelectableButton} from '../genericElements/Buttons';
import {useTranslation} from 'react-i18next';
import {FontSizeSelector, FontSizeSelectorProps} from './FontSizeSelector';

export interface NodeEditorRightSideProps {
  rootNode: XmlElementNode;
  originalNode: XmlElementNode;
  changed: boolean;
  applyUpdates: () => void;
  deleteNode?: () => void;
  cancelSelection: () => void;
  jumpElement?: (forward: boolean) => void;
  fontSizeSelectorProps: FontSizeSelectorProps;
  globalUpdateButtonRef?: RefObject<HTMLButtonElement>;
}

interface IProps extends NodeEditorRightSideProps {
  children: JSX.Element;
}

export function NodeEditorRightSide({
  rootNode,
  originalNode,
  children,
  changed,
  applyUpdates,
  deleteNode,
  jumpElement,
  fontSizeSelectorProps,
  cancelSelection,
  globalUpdateButtonRef
}: IProps): JSX.Element {

  const {t} = useTranslation('common');

  const handleJumpElement = (forward: boolean) => {
    if (!jumpElement) return;

    // Apply updates before jumping, similar to handleJumpKey in XmlDocumentEditor
    if (changed) {
      applyUpdates();
    }
    jumpElement(forward);
  };

  return (
    <div>
      <div className="p-4 rounded-t border border-slate-300 shadow-md">
        <NodeDisplay rootNode={rootNode} node={originalNode} isLeftSide={false}/>

        <div className="float-right">
          <FontSizeSelector {...fontSizeSelectorProps}/>

          {jumpElement &&
            <button type="button" className="ml-2 px-2 rounded border border-slate-500" onClick={() => handleJumpElement(false)}
                    title={t('previousTag') || 'previousTag'}>
              &larr;
            </button>}

          {deleteNode && <DeleteButton onClick={deleteNode} otherClasses={['ml-2', 'px-2', 'rounded']}/>}

          <SelectableButton selected={changed} onClick={applyUpdates} otherClasses={['ml-2', 'px-2', 'rounded']} ref={globalUpdateButtonRef}>
            <>{t('update')}</>
          </SelectableButton>

          <button type="button" className="ml-2 px-2 rounded border border-slate-500 bg-yellow-300" onClick={cancelSelection}
                  title={t('cancelSelection') || 'cancelSelection'}>
            &#x2715;
          </button>
          <button type="button" className="ml-2 px-2 rounded border border-slate-500 bg-green-500" onClick={applyUpdates}
                  title={t('applySelection') || 'applySelection'}>
            &#x2714;
          </button>

          {jumpElement &&
            <button type="button" className="ml-2 px-2 rounded border border-slate-500" onClick={() => handleJumpElement(true)} title={t('nextTag') || 'nextTag'}>
              &rarr;
            </button>}
        </div>
      </div>

      <div className="p-2 border border-slate-300" style={{fontSize: `${fontSizeSelectorProps.currentFontSize}%`}}>
        {children}
      </div>
    </div>
  );
}
